import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { InvoiceStatus } from "@api/shared-runtime/enums";
import { isAuthenticationRequiredMessage } from "@api/shared-runtime/billing-collection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { formatPlanCurrency } from "@/components/billing-plan/billing-plan-values";
import { useTRPC } from "@/trpc";
import {
  ADD_PAYMENT_METHOD_TO_PAY,
  PAYMENT_NEEDS_VERIFICATION_NOTICE,
  PAY_NOW_LABEL,
  RETRY_PAYMENT_LABEL,
  type CustomerInvoice,
} from "./invoice-presentation";
import {
  formatPaymentMethod,
  type SavedPaymentMethod,
} from "./payment-method-format";

export type PayableAccount = {
  version: number;
  paymentMethod: SavedPaymentMethod | null;
};

/** The same charge staff can run; shown only to members who manage invoices. */
export function PayInvoiceButton({
  invoice,
  organizationBusinessUnitId,
  account,
  size,
}: {
  invoice: CustomerInvoice;
  organizationBusinessUnitId: string;
  account: PayableAccount | null;
  size?: "sm";
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const payment = useMutation(
    trpc.organization.payInvoice.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          result.status === InvoiceStatus.Paid
            ? "Invoice paid"
            : "Payment submitted",
        );
      },
      onError: (error) => {
        toast.error("Payment failed", {
          description: isAuthenticationRequiredMessage(error.message)
            ? PAYMENT_NEEDS_VERIFICATION_NOTICE
            : error.message,
        });
      },
      // A decline changes the row (retry label, notice); a success changes
      // the status. Either way the list must be re-read.
      onSettled: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.organization.listInvoices.queryOptions({
              organizationBusinessUnitId,
            }).queryKey,
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.organization.getBillingAccount.queryOptions({
              organizationBusinessUnitId,
            }).queryKey,
          }),
        ]);
      },
    }),
  );

  if (!invoice.payable) {
    return null;
  }
  if (!account?.paymentMethod) {
    return (
      <p className="text-xs text-muted-foreground">
        {ADD_PAYMENT_METHOD_TO_PAY}
      </p>
    );
  }
  const label = invoice.paymentFailed ? RETRY_PAYMENT_LABEL : PAY_NOW_LABEL;
  const amount = formatPlanCurrency(
    invoice.amountDue ?? invoice.subtotalAmount,
  );
  const method = formatPaymentMethod(account.paymentMethod);
  return (
    <>
      <Button
        type="button"
        size={size}
        disabled={payment.isPending}
        onClick={() => setConfirming(true)}
      >
        {payment.isPending ? "Paying..." : label}
      </Button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pay this invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              {amount} will be charged to {method}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                setConfirming(false);
                payment.mutate({
                  organizationBusinessUnitId,
                  invoiceId: invoice.id,
                  expectedVersion: account.version,
                });
              }}
            >
              {label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
